import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Stack,
  Tooltip,
  Typography,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
} from '@mui/joy';
import {
  Palette as PaletteIcon,
  X as CloseIcon,
  Check as CheckIcon,
} from '@phosphor-icons/react';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  helpText?: string;
  brandColors?: {
    primary?: string;
    secondary?: string;
    tertiary?: string;
    quaternary?: string;
    quinary?: string;
  };
}

const PRESET_COLORS = [
  { label: 'White', value: '#FFFFFF' },
  { label: 'Black', value: '#000000' },
  { label: 'Gray', value: '#6B7280' },
  { label: 'Light Gray', value: '#F3F4F6' },
  { label: 'Red', value: '#DC2626' },
  { label: 'Blue', value: '#2563EB' },
  { label: 'Green', value: '#16A34A' },
  { label: 'Yellow', value: '#FACC15' },
  { label: 'Purple', value: '#9333EA' },
  { label: 'Pink', value: '#EC4899' },
  { label: 'Orange', value: '#FB923C' },
  { label: 'Teal', value: '#14B8A6' },
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label,
  helpText,
  brandColors,
}) => {
  const [open, setOpen] = useState(false);
  const [tempColor, setTempColor] = useState(value || '#FFFFFF');

  const handleOpen = () => {
    setOpen(true);
    setTempColor(value || '#FFFFFF');
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
    handleClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setTempColor(newColor);
    if (/^#[0-9A-F]{6}$/i.test(newColor)) {
      onChange(newColor);
    }
  };

  const clearColor = () => {
    onChange('');
    handleClose();
  };

  return (
    <>
      <FormControl>
        {label && <FormLabel>{label}</FormLabel>}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            startDecorator={
              value && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 'sm',
                    backgroundColor: value,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              )
            }
            endDecorator={
              <IconButton onClick={handleOpen} variant="plain">
                <PaletteIcon />
              </IconButton>
            }
            sx={{ flex: 1 }}
          />
          {value && (
            <Tooltip title="Clear color">
              <IconButton onClick={clearColor} variant="soft" color="neutral">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {helpText && (
          <Typography level="body-xs" sx={{ mt: 0.5 }}>
            {helpText}
          </Typography>
        )}
      </FormControl>

      <Modal open={open} onClose={handleClose}>
        <ModalDialog sx={{ width: 360, maxWidth: '90vw' }}>
          <DialogTitle>
            <Typography level="title-md">Choose Color</Typography>
            <IconButton
              variant="plain"
              color="neutral"
              onClick={handleClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
            {/* Brand Colors */}
            {(brandColors?.primary || brandColors?.secondary || brandColors?.tertiary || brandColors?.quaternary || brandColors?.quinary) && (
              <>
                <Typography level="title-sm">Brand Colors</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {brandColors.primary && (
                    <Button
                      onClick={() => handleColorSelect(brandColors.primary!)}
                      size="sm"
                      sx={{
                        backgroundColor: `${brandColors.primary} !important`,
                        color: 'white',
                        border: '2px solid',
                        borderColor: value === brandColors.primary ? 'primary.500' : 'transparent',
                        minWidth: 80,
                        '&:hover': {
                          backgroundColor: `${brandColors.primary} !important`,
                          opacity: 0.9,
                          borderColor: 'primary.300',
                        },
                      }}
                    >
                      Primary
                    </Button>
                  )}
                  {brandColors.secondary && (
                    <Button
                      onClick={() => handleColorSelect(brandColors.secondary!)}
                      size="sm"
                      sx={{
                        backgroundColor: `${brandColors.secondary} !important`,
                        color: 'white',
                        border: '2px solid',
                        borderColor: value === brandColors.secondary ? 'primary.500' : 'transparent',
                        minWidth: 80,
                        '&:hover': {
                          backgroundColor: `${brandColors.secondary} !important`,
                          opacity: 0.9,
                          borderColor: 'primary.300',
                        },
                      }}
                    >
                      Secondary
                    </Button>
                  )}
                  {brandColors.tertiary && (
                    <Button
                      onClick={() => handleColorSelect(brandColors.tertiary!)}
                      size="sm"
                      sx={{
                        backgroundColor: `${brandColors.tertiary} !important`,
                        color: 'white',
                        border: '2px solid',
                        borderColor: value === brandColors.tertiary ? 'primary.500' : 'transparent',
                        minWidth: 80,
                        '&:hover': {
                          backgroundColor: `${brandColors.tertiary} !important`,
                          opacity: 0.9,
                          borderColor: 'primary.300',
                        },
                      }}
                    >
                      Accent 1
                    </Button>
                  )}
                  {brandColors.quaternary && (
                    <Button
                      onClick={() => handleColorSelect(brandColors.quaternary!)}
                      size="sm"
                      sx={{
                        backgroundColor: `${brandColors.quaternary} !important`,
                        color: 'white',
                        border: '2px solid',
                        borderColor: value === brandColors.quaternary ? 'primary.500' : 'transparent',
                        minWidth: 80,
                        '&:hover': {
                          backgroundColor: `${brandColors.quaternary} !important`,
                          opacity: 0.9,
                          borderColor: 'primary.300',
                        },
                      }}
                    >
                      Accent 2
                    </Button>
                  )}
                  {brandColors.quinary && (
                    <Button
                      onClick={() => handleColorSelect(brandColors.quinary!)}
                      size="sm"
                      sx={{
                        backgroundColor: `${brandColors.quinary} !important`,
                        color: 'white',
                        border: '2px solid',
                        borderColor: value === brandColors.quinary ? 'primary.500' : 'transparent',
                        minWidth: 80,
                        '&:hover': {
                          backgroundColor: `${brandColors.quinary} !important`,
                          opacity: 0.9,
                          borderColor: 'primary.300',
                        },
                      }}
                    >
                      Accent 3
                    </Button>
                  )}
                </Box>
              </>
            )}

            {/* Preset Colors */}
            <Typography level="title-sm">Preset Colors</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
              }}
            >
              {PRESET_COLORS.map((color) => (
                <Tooltip key={color.value} title={color.label}>
                  <Box
                    onClick={() => handleColorSelect(color.value)}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: color.value,
                      border: '2px solid',
                      borderColor: value === color.value ? 'primary.500' : 'divider',
                      borderRadius: 'sm',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        borderColor: 'primary.300',
                      },
                    }}
                  >
                    {value === color.value && (
                      <CheckIcon
                        color={color.value === '#FFFFFF' ? 'var(--joy-palette-text-primary)' : 'white'}
                        size={16}
                      />
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {/* Custom Color Picker */}
            <Typography level="title-sm">Custom Color</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                type="color"
                value={tempColor}
                onChange={(e) => setTempColor(e.target.value)}
                style={{
                  width: '100%',
                  height: 40,
                  border: '1px solid #E5E7EB',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Input
              value={tempColor}
              onChange={handleInputChange}
              placeholder="#000000"
              size="sm"
            />

              {/* Actions */}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="sm" variant="plain" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleColorSelect(tempColor)}
                  disabled={!/^#[0-9A-F]{6}$/i.test(tempColor)}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </>
  );
};